import { KEYS } from "platejs";
import {
  BoldPlugin,
  ItalicPlugin,
  UnderlinePlugin,
  StrikethroughPlugin,
  CodePlugin,
} from "@platejs/basic-nodes/react";
import { HighlightPlugin } from "@platejs/basic-nodes/react";
import { BlockquotePlugin } from "@platejs/basic-nodes/react";
import {
  BulletedListPlugin,
  ListItemPlugin,
  ListItemContentPlugin,
  NumberedListPlugin,
} from "@platejs/list-classic/react";
import { LinkPlugin } from "@platejs/link/react";
import { HorizontalRulePlugin } from "@platejs/basic-nodes/react";
import { ParagraphPlugin } from "platejs/react";
import { ImagePlugin, PlaceholderPlugin } from "@platejs/media/react";
import { ColumnItemPlugin, ColumnPlugin } from "@platejs/layout/react";
import {
  TablePlugin,
  TableRowPlugin,
  TableCellPlugin,
  TableCellHeaderPlugin,
} from "@platejs/table/react";
import { IframePlugin } from "./plugins/IframePlugin";
import { DivPlugin } from "./plugins/DivPlugin";
import { ScriptPlugin } from "./plugins/ScriptPlugin";

export const NodeType = {
  PARAGRAPH: ParagraphPlugin.key,
  ORDERED_LIST: NumberedListPlugin.key,
  UNORDERED_LIST: BulletedListPlugin.key,
  LIST_ITEM: ListItemPlugin.key,
  LIST_ITEM_CONTENT: ListItemContentPlugin.key,
  THEMATIC_BREAK: HorizontalRulePlugin.key,
  BLOCK_QUOTE: BlockquotePlugin.key,
  HEADING_ONE: KEYS.h1,
  HEADING_TWO: KEYS.h2,
  HEADING_THREE: KEYS.h3,
  HEADING_FOUR: KEYS.h4,
  HEADING_FIVE: KEYS.h5,
  HEADING_SIX: KEYS.h6,
  LINK: LinkPlugin.key,
  IMAGE: ImagePlugin.key,
  MEDIA_PLACEHOLDER: PlaceholderPlugin.key,
  COLUMN_ITEM: ColumnItemPlugin.key,
  COLUMN_GROUP: ColumnPlugin.key,
  IFRAME: IframePlugin.key,
  SCRIPT: ScriptPlugin.key,
  DIV: DivPlugin.key,
  TABLE: TablePlugin.key,
  TABLE_ROW: TableRowPlugin.key,
  TABLE_CELL: TableCellPlugin.key,
  TABLE_CELL_HEADER: TableCellHeaderPlugin.key,
};

export const MarkType = {
  EMPHASIS: ItalicPlugin.key,
  STRONG: BoldPlugin.key,
  UNDERLINE: UnderlinePlugin.key,
  STRIKETHROUGH: StrikethroughPlugin.key,
  CODE: CodePlugin.key,
  HIGHLIGHT: HighlightPlugin.key,
};

export const markdownDefaultMaxLength = 200000;
export const defaultValue = [
  {
    type: NodeType.PARAGRAPH,
    children: [{ text: "" }],
  },
];

const isBlankText = (node: ChildNode | null) =>
  node?.nodeType === Node.TEXT_NODE && !node.textContent?.trim();

// Authors indent their table markup, and with collapseWhiteSpace: false those newlines survive as
// text nodes inside and beside the table. Slate can hold no text inside a table, so Plate hoists each
// one out and normalizes it into an empty paragraph; the ones beside the table become empty
// paragraphs where they sit. A table is a block, so none of that whitespace renders.
export function stripWhitespaceBetweenTableTags(html: string) {
  if (!/<table/i.test(html)) return html;
  const root = document.createElement("div");
  root.innerHTML = html;
  for (const element of root.querySelectorAll(
    "table, thead, tbody, tfoot, tr",
  )) {
    for (const child of Array.from(element.childNodes)) {
      if (isBlankText(child)) child.remove();
    }
  }
  for (const table of root.querySelectorAll("table")) {
    for (const sibling of [table.previousSibling, table.nextSibling]) {
      if (isBlankText(sibling)) sibling!.remove();
    }
  }
  return root.innerHTML;
}
